import React, { useState, useEffect, useRef } from 'react';
import { Play, RotateCcw, Sliders, AlertCircle, RefreshCw } from 'lucide-react';

const OPTIONS = [1000, 10000, 50000, 200000];

const KEY_PLAYERS = [
  { teamId: 'fra', name: 'France', player: 'Kylian Mbappé' },
  { teamId: 'bra', name: 'Brésil', player: 'Vinícius Júnior' },
  { teamId: 'arg', name: 'Argentine', player: 'Lionel Messi' },
  { teamId: 'esp', name: 'Espagne', player: 'Lamine Yamal' },
  { teamId: 'eng', name: 'Angleterre', player: 'Jude Bellingham' },
  { teamId: 'por', name: 'Portugal', player: 'Cristiano Ronaldo' }
];

export function SimulationControls({ state, onRun, onReset }) {
  const [selectedSims, setSelectedSims] = useState(10000);
  const [showSandbox, setShowSandbox] = useState(false);
  
  // Custom states for what-if parameters
  const [eloAdjustments, setEloAdjustments] = useState({});
  const [customAbsences, setCustomAbsences] = useState({});
  
  const startRef = useRef(null);
  const isRunning = state.status === 'running';
  const isDone = state.status === 'done';
  const pct = state.total > 0 ? (state.completed / state.total) * 100 : 0;

  useEffect(() => {
    if (isRunning && startRef.current === null) startRef.current = performance.now();
    if (!isRunning) startRef.current = null;
  }, [isRunning]);

  const remaining =
    isRunning && state.completed > 0 && startRef.current !== null
      ? Math.max(0, Math.round(((performance.now() - startRef.current) / state.completed) * (state.total - state.completed) / 1000))
      : null;

  const handleEloChange = (teamId, val) => {
    setEloAdjustments(prev => ({
      ...prev,
      [teamId]: val
    }));
  };

  const handleAbsenceToggle = (teamId, playerName) => {
    setCustomAbsences(prev => {
      const current = prev[teamId] || [];
      const updated = current.includes(playerName)
        ? current.filter(p => p !== playerName)
        : [...current, playerName];
      return {
        ...prev,
        [teamId]: updated
      };
    });
  };

  const resetSandbox = () => {
    setEloAdjustments({});
    setCustomAbsences({});
  };

  const hasAdjustments = 
    Object.values(eloAdjustments).some(v => v !== 0) || 
    Object.values(customAbsences).some(arr => arr.length > 0);

  const startSimulation = () => {
    // Send selected iterations, a random or stable seed, and the parameters
    onRun(selectedSims, 42, eloAdjustments, customAbsences);
  };

  return (
    <div className="card-elev p-5 sm:p-6 mb-8 border border-[#2A4A6B]">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        
        {/* Run Controls */}
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-[0.18em] text-gray-400 font-mono">
            Nombre de simulations
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {OPTIONS.map((n) => {
              const active = selectedSims === n;
              return (
                <button
                  key={n}
                  disabled={isRunning}
                  onClick={() => setSelectedSims(n)}
                  className={`rounded-full border px-4 py-1.5 font-mono text-xs tracking-wider transition-all ${
                    active
                      ? 'border-[#00C853] bg-[#00C853]/10 text-white font-bold'
                      : 'border-[#2A4A6B] bg-transparent text-gray-300 hover:border-gray-400 hover:text-white'
                  } ${isRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {n >= 1000 ? `${n / 1000}K` : n}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Plus il y a de simulations, plus les probabilités estimées sont précises.
          </p>
        </div>

        {/* Action Button */}
        <div className="w-full shrink-0 lg:w-auto lg:min-w-[16rem]">
          <button
            onClick={startSimulation}
            disabled={isRunning}
            className={`group relative h-14 w-full overflow-hidden rounded-xl border transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center font-heading font-bold uppercase tracking-wider text-sm ${
              isRunning
                ? 'border-[#2A4A6B] bg-[#1E3A5F]/50 text-gray-300'
                : 'border-[#00C853] bg-gradient-to-b from-[#00C853] to-[#009E43] text-black shadow-[0_0_15px_rgba(0,200,83,0.2)]'
            }`}
          >
            {/* Progress Bar background */}
            {isRunning && (
              <span
                className="absolute inset-0 bg-[#00C853]/25 origin-left"
                style={{ transform: `scaleX(${pct / 100})`, transition: 'transform 150ms linear' }}
              />
            )}

            <span className="relative z-10 flex items-center justify-center gap-2 px-4">
              {isRunning ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin text-[#00C853]" />
                  <span className="font-mono text-sm tabular-nums">
                    {state.scenario === 'withAbsences' ? 'Passage 1/2' : 'Passage 2/2'} · {Math.round(pct)}%
                  </span>
                  {remaining !== null && (
                    <span className="font-mono text-xs text-gray-400">({remaining}s)</span>
                  )}
                </>
              ) : isDone ? (
                <>
                  <RotateCcw className="h-4 w-4" />
                  <span>Relancer la simulation</span>
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 fill-black" />
                  <span>Simuler le tournoi</span>
                </>
              )}
            </span>
          </button>
          {isDone && !isRunning && (
            <div className="text-center mt-2">
              <button 
                onClick={onReset}
                className="text-xs text-red-400 hover:underline inline-flex items-center gap-1"
              >
                Réinitialiser les résultats
              </button>
            </div>
          )}
        </div>
      </div>

      {/* What-If Sandbox Toggle */}
      <div className="mt-6 pt-5 border-t border-[#2A4A6B]">
        <button
          onClick={() => setShowSandbox(!showSandbox)}
          className="flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors focus:outline-none"
        >
          <Sliders size={16} className={hasAdjustments ? 'text-[#00C853]' : 'text-gray-400'} />
          <span className="font-heading font-semibold">
            Bac à sable "What-If" {hasAdjustments && <span className="text-xs text-[#00C853] bg-[#00C853]/10 px-2 py-0.5 rounded-full ml-1 font-mono">actif</span>}
          </span>
          <span className="text-xs text-gray-500">
            ({showSandbox ? 'Cliquer pour masquer' : 'Cliquer pour ouvrir le configurateur'})
          </span>
        </button>

        {showSandbox && (
          <div className="mt-4 p-4 rounded-xl bg-[#1E3A5F]/20 border border-[#2A4A6B] animate-fadeIn">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-xs font-bold text-white uppercase tracking-wider">
                Ajustez les forces et absences pour simuler vos hypothèses
              </h4>
              {hasAdjustments && (
                <button
                  onClick={resetSandbox}
                  className="text-xs text-red-400 hover:underline"
                >
                  Tout effacer
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {KEY_PLAYERS.map(({ teamId, name, player }) => {
                const eloVal = eloAdjustments[teamId] || 0;
                const isInjured = (customAbsences[teamId] || []).includes(player);

                return (
                  <div key={teamId} className="p-3 rounded-lg bg-[#1E3A5F]/35 border border-[#2D4D73]/30">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-heading font-bold text-white text-sm">{name}</span>
                      <span className={`text-xs font-mono font-semibold ${eloVal > 0 ? 'text-[#00C853]' : eloVal < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                        {eloVal > 0 ? `+${eloVal}` : eloVal} ELO
                      </span>
                    </div>

                    {/* ELO Slider */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[10px] text-gray-500 font-mono">-300</span>
                      <input
                        type="range"
                        min="-300"
                        max="300"
                        step="10"
                        value={eloVal}
                        disabled={isRunning}
                        onChange={(e) => handleEloChange(teamId, parseInt(e.target.value))}
                        className="flex-1 accent-[#00C853] bg-[#2A4A6B]/50 h-1 rounded-lg cursor-pointer"
                      />
                      <span className="text-[10px] text-gray-500 font-mono">+300</span>
                    </div>

                    {/* Injury Checkbox */}
                    <label className="flex items-center gap-2 cursor-pointer text-xs select-none">
                      <input
                        type="checkbox"
                        checked={isInjured}
                        disabled={isRunning}
                        onChange={() => handleAbsenceToggle(teamId, player)}
                        className="rounded border-[#2A4A6B] text-[#00C853] focus:ring-[#00C853] bg-transparent cursor-pointer"
                      />
                      <span className={`transition-colors ${isInjured ? 'text-red-400 font-semibold' : 'text-gray-400'}`}>
                        Simuler la blessure de {player}
                      </span>
                    </label>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-4 flex items-start gap-2 text-[11px] text-gray-500 bg-[#1E3A5F]/10 p-2.5 rounded-lg border border-[#2A4A6B]/30">
              <AlertCircle size={14} className="text-[#FFB300] shrink-0 mt-0.5" />
              <span>
                <strong>Note :</strong> Modifier ces paramètres affectera le score ELO effectif des équipes lors de la simulation. La blessure simulée d'un joueur applique un malus ELO proportionnel à son importance dans l'équipe.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
